<template>
  <div class="list-container">
    <div class="top-fix-area" ref="topFixArea">
      <div class="d-flex justify-content-between align-items-center">
        <h1 class="page-title">業者別作業予定表 - 業者情報一覧</h1>
        <router-link :to="{ name: 'Home' }"> <!-- 業者情報詳細ページへのリンク -->
          <button type="button" class="btn btn-primary mx-1">ホーム</button>
        </router-link>
      </div>

      <!-- 検索バーの追加 -->
      <SearchPanelWrapper :key="route.fullPath" v-model="isSearchExpanded" :getTableElement="() => tableWrapper?.$el || tableWrapper?.value" >
        <form class="search-container" style="background-color: #f8f9fa;" @submit.prevent="searchVendors"> <!-- 背景色を薄く設定 -->
          <div class="row g-3 align-items-center">
            <div class="d-flex align-items-center mb-2 me-2">
              <div class="me-2">
                <label class="col-form-label">予定年月</label>
              </div>
              <div class="d-flex align-items-center">
                <select id="searchYear" class="form-select" v-model="searchYear" placeholder="年で検索">
                  <option value="" disabled selected>選択してください</option> <!-- placeholder -->
                  <option v-for="year in years" :key="year" :value="year">{{ year }}年</option>
                </select>

                <select id="searchMonth" class="form-select ms-2" v-model="searchMonth" placeholder="月で検索">
                  <option value="" disabled selected>選択してください</option> <!-- placeholder -->
                  <option v-for="month in months" :key="month" :value="month">{{ month }}月</option>
                </select>
              </div>

              <!-- 予定表発行フラグ -->
              <div class="d-flex align-items-center ms-2">
                <select class="form-select me-1" v-model="searchHasVendorSchedule">
                  <option value="" selected>全て</option>
                  <option value="1">予定表作成済み</option>
                  <option value="0">予定表未作成</option>
                </select>
              </div>

              <div class="ms-3">
                <button type="submit" class="btn btn-sm btn-primary mx-1">検索</button>
                <button type="button" class="btn btn-sm btn-secondary mx-1" @click="searchResetVendors">クリア</button>
              </div>
            </div>
          </div>
        </form>
      </SearchPanelWrapper>
    </div>

    <div class="d-flex justify-content-end" ref="paginationContainer">
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div class="table-wrapper" ref="tableWrapper">
      <table class="master-table table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="btn-cell"></th>
            <th class="text-nowrap">業者名</th>
            <th class="text-nowrap">住所</th>
            <th class="text-nowrap">メールアドレス</th>
            <th class="text-nowrap">電話番号</th>
            <th class="text-nowrap">FAX</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="vendor in vendors" :key="vendor.id">
            <td  class="btn-cell text-nowrap">
              <router-link v-if="!(vendor.has_vendor_schedule)" :to="{ name: 'VendorWorkMonthlyCalendarCreate', params: { vendor_id: vendor.id, year: targetYear, month: targetMonth }, query: getSearchQuery() }"> <!-- 業者情報詳細ページへのリンク -->
                <button type="button" class="btn btn-sm btn-primary mx-1">予定表作成</button>
              </router-link>

              <router-link v-if="vendor.has_vendor_schedule" :to="{ name: 'VendorWorkMonthlyCalendarDetail', params: { vendor_id: vendor.id, year: targetYear, month: targetMonth }, query: getSearchQuery() }"> <!-- 業者情報編集ページへのリンク -->
                <button type="button" class="btn btn-sm btn-info mx-1">予定表詳細</button>
              </router-link>
            </td>
            <td>{{ vendor.vendor_nm }}</td>
            <td>{{vendor.prefecture}}{{vendor.city}}{{vendor.other_address}}</td>
            <td class="text-nowrap">{{ vendor.email }}</td>
            <td class="text-nowrap">{{ vendor.tel_num }}</td>
            <td class="text-nowrap">{{ vendor.fax }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { getMonthlyWorkVendors } from '@/api/axios';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import Pagination from '@/components/shared/Pagination.vue';

export default {
  components: {
    SearchPanelWrapper,
    Pagination
  },
  setup() {
    const title = ref('月別請負業者情報一覧');

    watchEffect(() => {
      document.title = title.value;
    });

    const router = useRouter();
    const route = useRoute();
    const vendors = ref([]);
    const years = ref([]);
    const months = ref([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
    const targetYear = ref('');
    const targetMonth = ref('');

    // 翌月を取得
    const currentDate = new Date();
    let nextYear = currentDate.getFullYear();
    let nextMonth = currentDate.getMonth() + 2; // 現在の月に1を加算（0-indexedなので+1が必要）

    if (nextMonth > 11) { // 月が12を超える場合
      nextMonth = 1; // 翌月は1月（0-indexed）
      nextYear += 1; // 年を繰り上げ
    }

    for (let i = nextYear - 5; i <= nextYear + 5; i++) {
      years.value.push(i);
    }

    targetYear.value = route.query.vendor_schedule_year ? parseInt(route.query.vendor_schedule_year, 10) : nextYear;
    targetMonth.value = route.query.vendor_schedule_month ? parseInt(route.query.vendor_schedule_month, 10) : nextMonth;

    // 検索用のデータプロパティ
    const searchYear = ref('');
    const searchMonth = ref('');
    const searchHasVendorSchedule = ref('');
    searchYear.value = targetYear.value;
    searchMonth.value = targetMonth.value;

    // テーブルの高さの動的な設定
    const topFixArea = ref(null);
    const tableWrapper = ref(null);
    const paginationContainer = ref(null);

    // 検索パネルの展開状態を管理するためのリアクティブプロパティ
    const isSearchExpanded = ref(true);

    // ▼▼ 共通：検索条件をそのまま query として返す ▼▼
    const getSearchQuery = () => ({
      vendor_schedule_year: searchYear.value,
      vendor_schedule_month: searchMonth.value,
      vendor_schedule_has: searchHasVendorSchedule.value,
      vendor_schedule_page: currentPage.value || 1
    });

    // ページネーション
    const currentPage = ref(1);
    const totalPages = ref(50); // トータルページ数を管理するためのプロパティ

    const fetchVendors = async (page = 1) => {
      try {
        const response = await getMonthlyWorkVendors({
          year: searchYear.value,
          month: searchMonth.value,
          has_vendor_schedule: searchHasVendorSchedule.value,
          page: page
        });
        vendors.value = response.data.vendors;
        totalPages.value = response.data.meta.total_pages;
        currentPage.value = response.data.meta.current_page;
      } catch (error) {
        console.error('業者情報ーデータの取得に失敗しました:', error);
      }
    };

    const handleChangePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        fetchVendors(page);

        const q = getSearchQuery();
        q.vendor_schedule_page = page;

        router.replace({
          path: '/monthly-work-vendors',
          query: q,
        });

        // スクロールを tableWrapper のトップへ
        if (tableWrapper.value) {
          tableWrapper.value.scrollTop = 0;
        }
      }
    };

    // 業者を検索するメソッド
    const searchVendors = async () => {
      await fetchVendors(1);
      targetYear.value = searchYear.value;
      targetMonth.value = searchMonth.value;

      const q = getSearchQuery();
      q.vendor_schedule_page = 1;

      // クエリをルートに反映
      router.push({
        path: '/monthly-work-vendors',
        query: q,
      });
    };

    // 業者を検索リセットするメソッド
    const searchResetVendors = async () => {
      const currentDate = new Date();
      let nextYear = currentDate.getFullYear();
      let nextMonth = currentDate.getMonth() + 2; // 現在の月に1を加算（0-indexedなので+1が必要）

      if (nextMonth > 11) { // 月が12を超える場合
        nextMonth = 1; // 翌月は1月（0-indexed）
        nextYear += 1; // 年を繰り上げ
      }

      searchYear.value = nextYear;
      searchMonth.value = nextMonth;
      searchHasVendorSchedule.value = '';
      targetYear.value = nextYear;
      targetMonth.value = nextMonth;

      await fetchVendors(1);

      const q = getSearchQuery();
      q.vendor_schedule_page = 1;

      // クエリをリセット
      router.replace({
        path: '/monthly-work-vendors',
        query: q,
      });
    };

    // ▼▼▼ クエリパラメータから検索条件を復元 ▼▼▼
    onMounted(() => {
      const q = route.query;

      searchYear.value = q.vendor_schedule_year ? Number(q.vendor_schedule_year) : targetYear.value;
      searchMonth.value = q.vendor_schedule_month ? Number(q.vendor_schedule_month) : targetMonth.value;
      searchHasVendorSchedule.value = q.vendor_schedule_has || '';
      currentPage.value = q.vendor_schedule_page ? Number(q.vendor_schedule_page) : 1;

      targetYear.value = searchYear.value;
      targetMonth.value = searchMonth.value;

      fetchVendors(currentPage.value);
    });

    return {
      title,
      route,
      vendors,
      years,
      months,
      targetYear,
      targetMonth,
      searchYear,
      searchMonth,
      searchHasVendorSchedule,
      getSearchQuery,
      searchVendors,
      searchResetVendors,
      topFixArea,
      tableWrapper,
      paginationContainer,
      isSearchExpanded,
      currentPage,
      totalPages,
      handleChangePage
    };
  }
};
</script>

<style scoped>
.list-container {
  padding: 20px 20px 80px 20px;
  margin: auto;
}

.vendor-item {
  border: 1px solid #ddd;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.vendor-item:hover {
  background-color: #f5f5f5;
}

table thead th {
  background: #003366 !important; /* 背景色を設定し、スクロール中に背景が透けるのを防ぐ */
  color: #fff !important;
}

table th,
table td{
  font-size: 14px;
}

table .btn-cell{
  width: 132px;
  min-width: 132px;
  max-width: 132px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.pagination-container button {
  margin: 0 10px;
}
</style>
